import TooltipIcon from 'src/components/ui/tooltip';
import './styles.scss';

import { ComboBox } from 'src/components/ui/combobox';
import { getI18n } from 'react-i18next';
import { Portfolio } from 'src/services/models/portfolio.service';
import { PortfolioService } from 'src/services/portfolio.service';
import { useLocalization } from 'src/utils/useLocalization';
import {
    AccountStatus,
    PortfolioSearch,
} from 'src/utils/enums/portfolio.enums';
import {
    compareDates,
    compareNumbers,
    compareStrings,
} from 'src/utils/helpers';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { matchAccountNumber, matchVIN } from 'src/utils/validators';
interface ISearchProps {
    updateTableContent: Dispatch<SetStateAction<Portfolio[]>>;
    dealerID: string;
    setLoading: Dispatch<SetStateAction<boolean>>;
    sortType: string;
    sortOrder: boolean;
    brand: string;
    country: string;
    accFilter: string;
}

export const Search: React.FC<ISearchProps> = ({
    updateTableContent,
    dealerID,
    setLoading,
    sortType,
    sortOrder,
    brand,
    country,
    accFilter,
}) => {
    const { paymentHistory, portfolioManager } = useLocalization();

    const dateFilterOptions: string[] = [
        portfolioManager.PORTFOLIO_SEARCH_MONTHS_3,
        portfolioManager.PORTFOLIO_SEARCH_MONTHS_6,
        portfolioManager.PORTFOLIO_SEARCH_MONTHS_9,
        portfolioManager.PORTFOLIO_SEARCH_MONTHS_12,
    ];

    const [searchFilter, setSearchFilter] = useState<string>(
        PortfolioSearch.MONTHS,
    );
    const [searchValue, setSearchValue] = useState<string>('3');
    const [dateFilter, setDateFilter] = useState<string>(dateFilterOptions[0]);
    const [programFilter, setProgramFilter] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [especialPrograms, setEspecialPrograms] = useState<any[]>([]);
    const [records, setRecords] = useState<any[]>([]);

    const tooltip = portfolioManager.PORTFOLIO_SEARCH_BY_NAME_TOOLTIP;
    const specialTooltip = portfolioManager.PORTFOLIO_SEARCH_BY_SPECIAL_TOOLTIP;
    const resetFilters = async () => {
        setDateFilter(dateFilterOptions[0]);
        setProgramFilter('');
        setSearchTerm('');
        setSearchFilter(PortfolioSearch.MONTHS);
        setSearchValue('3');
    };

    const handleReset = async () => {
        await resetFilters();
    };

    const searchData = async () => {
        try {
            setLoading(true);
            const service = new PortfolioService();
            const response = await service.search(
                dealerID,
                searchFilter,
                searchValue,
                accFilter,
            );
            if (response?.data) {
                setRecords(
                    compareDates([...response.data], 'maturityDate', false),
                );
            }
            setLoading(false);
        } catch {
            setRecords([]);
            setLoading(false);
        }
    };

    function capitalizeFirstLetter(char: string) {
        return (
            char.charAt(Number('0')).toLocaleUpperCase() +
            char.slice(Number('1'))
        );
    }

    const getPrograms = async () => {
        try {
            const service = new PortfolioService();
            const res = await service.specialPrograms(
                dealerID,
                country,
                brand === 'vw'
                    ? brand.toLocaleUpperCase()
                    : capitalizeFirstLetter(brand),
            );
            if (res?.data) {
                setEspecialPrograms(
                    res.data.map((ele: any) => ele.programCode),
                );
            }
        } catch (e) {
            console.log('Error getting special programs');
        }
    };

    useEffect(() => {
        getPrograms();
    }, []);

    useEffect(() => {
        updateTableContent(filterData());
    }, [records]);

    useEffect(() => {
        updateTableContent(filterData());
    }, [sortOrder, sortType, records]);

    useEffect(() => {
        getPrograms();
    }, [searchValue]);

    /**
     * Function to apply sort based on sort type and sor order
     * @param data
     * @returns
     */
    const applySort = (data: any[]) => {
        let sorted = [...data];
        switch (sortType) {
            case 'blackEquity':
                sorted = compareNumbers(sorted, 'blackEquity', sortOrder);
                break;
            case 'maturityDate':
                sorted = compareDates(sorted, 'maturityDate', sortOrder);
                break;
            case 'termStatusDate':
                sorted = compareDates(sorted, 'termStatusDate', sortOrder);
                break;
            case 'vehicleModel':
                sorted = compareStrings(sorted, 'vehicleModel', sortOrder);
                break;
            default:
                sorted = compareDates(sorted, 'maturityDate', false);
                break;
        }
        return sorted;
    };

    const filterData = () => {
        const filteredData = [...records];
        return applySort(filteredData);
    };

    useEffect(() => {
        searchData();
    }, []);

    useEffect(() => {
        setDateFilter(dateFilterOptions[0]);
        searchData();
    }, [accFilter]);

    useEffect(() => {
        resetFilters();
    }, [getI18n().language]);

    useEffect(() => {
        if (dateFilter) {
            setSearchFilter(PortfolioSearch.MONTHS);
            switch (dateFilter) {
                case dateFilterOptions[1]:
                    setSearchValue('6');
                    break;
                case dateFilterOptions[2]:
                    setSearchValue('9');
                    break;
                case dateFilterOptions[3]:
                    setSearchValue('12');
                    break;
                default:
                    setSearchValue('3');
                    break;
            }
            setProgramFilter('');
            setSearchTerm('');
        }
    }, [dateFilter]);

    useEffect(() => {
        if (programFilter) {
            setSearchFilter(PortfolioSearch.PROGRAM);
            setSearchValue(programFilter);
            setDateFilter('');
            setSearchTerm('');
        }
    }, [programFilter]);

    useEffect(() => {
        if (searchValue) searchData();
    }, [searchValue]);

    const handleEnter = (evt: any) => {
        if (evt.key === 'Enter') {
            searchData();
        }
    };

    useEffect(() => {
        if (searchTerm && searchTerm.length > Number('3')) {
            applySearch();
            setDateFilter('');
            setProgramFilter('');
        }
    }, [searchTerm]);

    async function applySearch() {
        if (matchAccountNumber(searchTerm)) {
            setSearchFilter(PortfolioSearch.ACCOUNT);
        } else if (matchVIN(searchTerm)) {
            setSearchFilter(PortfolioSearch.VIN);
        } else {
            setSearchFilter(PortfolioSearch.NAME);
        }
        setSearchValue(searchTerm);
    }

    return (
        <div className='portfolio-search-bar search-row c-input'>
            <div className='search-div'>
                <div className='c-input search-text'>
                    <span id='searchIcon'>
                        <i
                            className='c-icon c-icon--[semantic-search]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                    <input
                        id='searchInput'
                        className='c-input__input'
                        type={'text'}
                        value={searchTerm}
                        placeholder={portfolioManager.PORTFOLIO_SEARCH_LABEL}
                        onChange={(evt) => setSearchTerm(evt.target.value)}
                        onKeyDown={handleEnter}
                        role='searchQuery'
                    />
                    <span
                        id='clearSearch'
                        className={`c-input__addon ${
                            !searchTerm ? 'invisible' : ''
                        }`}
                        onClick={() => handleReset()}
                        style={{ borderColor: '#d1dcde' }}
                        data-testid='clearSearch'
                        role='clearSearch'
                    >
                        <i
                            className='c-icon  c-icon--[semantic-close]'
                            aria-hidden='true'
                            role={'img'}
                        ></i>
                    </span>
                </div>
                <TooltipIcon text={tooltip}></TooltipIcon>
            </div>
            <div className='select-ph-date'>
                <ComboBox
                    name={'date'}
                    options={dateFilterOptions}
                    defaultValue={dateFilterOptions[0]}
                    placeholder={portfolioManager.PORTFOLIO_SEARCH_DATE_TITLE}
                    onSelect={setDateFilter}
                    selectedOption={dateFilter}
                />
            </div>
            <div className='select-ph-status'>
                <ComboBox
                    name={'programs'}
                    options={especialPrograms}
                    defaultValue={especialPrograms[0]}
                    placeholder={
                        portfolioManager.PORTFOLIO_SEARCH_PROGRAMS_TITLE
                    }
                    disabled={accFilter === AccountStatus.TERMINATED}
                    selectedOption={programFilter}
                    onSelect={setProgramFilter}
                />
                <TooltipIcon text={specialTooltip}></TooltipIcon>
            </div>
            <div className='reset-ph-search'>
                <button
                    className={'c-btn--secondary c-btn c-btn--small'}
                    type={'button'}
                    onClick={() => {
                        resetFilters();
                    }}
                >
                    <span className='c-btn__text'>
                        {paymentHistory.SEARCH_RESET}
                    </span>
                </button>
            </div>
        </div>
    );
};

export default Search;
